/** Routing and the query client. Everything under /g/:gid sits inside the group shell. */

import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import { GroupLayout } from "./components/layout/AppShell";
import { Spinner } from "./components/ui/spinner";
import { AdminPage } from "./features/admin/AdminPage";
import { AssistantPage } from "./features/ai/AssistantPage";
import { MembersPage } from "./features/admin/MembersPage";
import { SuperadminPage } from "./features/admin/SuperadminPage";
import {
  ForgotPasswordPage,
  LoginPage,
  RegisterPage,
  ResetPage,
  VerifyEmailPage,
} from "./features/auth/AuthPages";
import { SettingsPage } from "./features/auth/SettingsPage";
import { UnsubscribePage } from "./features/auth/UnsubscribePage";
import { PushNavigation, PushResync } from "./features/auth/PushSettings";
import { ConnectPage } from "./features/connect/ConnectPage";
import { SessionProvider, useSession } from "./features/auth/session";
import { BalancesPage } from "./features/balances/BalancesPage";
import { DashboardPage } from "./features/dashboard/DashboardPage";
import { EntryForm } from "./features/entry/EntryForm";
import { LedgerPage } from "./features/ledger/LedgerPage";
import { StatsPage } from "./features/stats/StatsPage";
import { CheckIcon } from "./components/ui/icons";
import { ApiError } from "./lib/api";
import { applyTheme, readTheme, watchSystemTheme } from "./lib/theme";
import { cn } from "./lib/utils";
import { startReplay } from "./lib/offline";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 or a 403 will not get better by asking again.
      retry: (failureCount, error) =>
        !(error instanceof ApiError && error.status < 500) && failureCount < 2,
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
});

/** Replays anything queued while offline, and says so once it lands. */
function OfflineQueue() {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);

  useEffect(
    () =>
      startReplay((result) => {
        setMessage(
          `${result.sent} queued ${result.sent === 1 ? "entry" : "entries"} uploaded.`,
        );
        void queryClient.invalidateQueries();
        setTimeout(() => setMessage(null), 5000);
      }),
    [queryClient],
  );

  if (!message) return null;
  return (
    <p
      role="status"
      className="fixed inset-x-0 top-0 z-50 bg-accent px-4 py-2 text-center text-sm text-accent-ink"
    >
      {message}
    </p>
  );
}

/**
 * Keeps the palette in step with the OS while the preference is "Auto".
 *
 * At the root rather than in `ThemeToggle`, which only renders once you are signed in —
 * so the OS switching to dark while somebody sat on the sign-in page used to do nothing.
 */
function SystemThemeWatcher() {
  useEffect(
    () =>
      watchSystemTheme(() => {
        if (readTheme() === "system") applyTheme("system");
      }),
    [],
  );
  return null;
}

/** How long the confirmation toast stays up before it fades out. */
const CONFIRMED_TOAST_MS = 5000;
/** Matches the `fade-out` animation, so the node leaves after it has finished. */
const TOAST_FADE_MS = 120;

/**
 * "Email confirmed" after following a signup link.
 *
 * A floating toast rather than a full-width band: this is a passing acknowledgement, not
 * a state the page is in, and a coloured bar pinned across the top reads as the latter.
 * It sits over the app because confirming lands you signed in and looking at your groups,
 * which is what you came for — an interstitial page would be a screen to dismiss.
 *
 * `?confirmed=1` is dropped from the URL immediately, so a reload — or sharing the link —
 * does not show it again.
 */
function ConfirmedToast() {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<"hidden" | "showing" | "leaving">(() =>
    params.get("confirmed") === "1" ? "showing" : "hidden",
  );

  useEffect(() => {
    if (params.get("confirmed") !== "1") return;
    setState("showing");
    const next = new URLSearchParams(params);
    next.delete("confirmed");
    setParams(next, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    if (state !== "showing") return;
    const fade = setTimeout(() => setState("leaving"), CONFIRMED_TOAST_MS);
    return () => clearTimeout(fade);
  }, [state]);

  useEffect(() => {
    if (state !== "leaving") return;
    const gone = setTimeout(() => setState("hidden"), TOAST_FADE_MS);
    return () => clearTimeout(gone);
  }, [state]);

  if (state === "hidden") return null;
  return (
    <div
      // `pointer-events-none` on the wrapper so the toast never blocks a tap on whatever
      // is underneath it; the dismiss button opts itself back in.
      //
      // Offset well clear of the header rather than pinned to the viewport top — at 1rem
      // it landed straight across "The Sheet" and the account links.
      className="pointer-events-none fixed inset-x-0 z-50 flex justify-center px-4"
      style={{ top: "calc(max(1rem, env(safe-area-inset-top)) + 3.5rem)" }}
    >
      <div
        role="status"
        className={cn(
          // `surface` is the shared raised-panel class: rounded, hairline border, one
          // soft shadow. Without it the toast is bare text floating on the page.
          "surface pointer-events-auto flex max-w-[calc(100vw-2rem)] items-center gap-2.5",
          "py-2.5 pl-3 pr-2 text-sm",
          state === "leaving" ? "animate-fade-out" : "animate-scale-in",
        )}
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-owed/15 text-owed">
          <CheckIcon className="h-3 w-3" title="Confirmed" />
        </span>
        <span className="min-w-0">Email confirmed — you are signed in.</span>
        <button
          type="button"
          onClick={() => setState("leaving")}
          aria-label="Dismiss"
          className="ml-1 shrink-0 rounded-md px-1.5 py-1 text-xs text-muted transition-colors hover:bg-ink/[0.06] hover:text-ink"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useSession();
  const location = useLocation();
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!user) {
    // Remember where they were going, so signing in takes them back there.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <SessionProvider>
          <SystemThemeWatcher />
          <PushResync />
          <PushNavigation />
          <OfflineQueue />
          <ConfirmedToast />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot" element={<ForgotPasswordPage />} />
            <Route path="/reset/:token" element={<ResetPage />} />
            {/* The target of the emailed confirmation link; spends the token on arrival. */}
            <Route path="/verify/:token" element={<VerifyEmailPage />} />
            {/* The weekly email's "turn it off" link. Signed, so no sign-in needed. */}
            <Route path="/unsubscribe" element={<UnsubscribePage />} />
            <Route
              path="/connect/:clientId"
              element={
                <RequireAuth>
                  <ConnectPage />
                </RequireAuth>
              }
            />

            <Route
              path="/"
              element={
                <RequireAuth>
                  <DashboardPage />
                </RequireAuth>
              }
            />

            <Route
              path="/g/:gid"
              element={
                <RequireAuth>
                  <GroupLayout />
                </RequireAuth>
              }
            >
              <Route index element={<LedgerPage />} />
              <Route path="balances" element={<BalancesPage />} />
              <Route path="stats" element={<StatsPage />} />
              <Route path="ai" element={<AssistantPage />} />
              <Route path="members" element={<MembersPage />} />
              <Route path="admin" element={<AdminPage />} />
              <Route path="new" element={<EntryForm />} />
              <Route path="t/:tid/edit" element={<EntryForm />} />
            </Route>

            <Route
              path="/settings"
              element={
                <RequireAuth>
                  <SettingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <SuperadminPage />
                </RequireAuth>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SessionProvider>
      </Router>
    </QueryClientProvider>
  );
}
